import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useMsal } from "@azure/msal-react";
import { initializeApiCall, AuthError } from '../../utils/apiUtils';
import { getUserEmail, loginRequest } from '../auth/authConfig';
import {
    getStoredSessionId as getAnonymousSessionId,
    clearSession as clearAnonymousSession,
} from '../../services/anonymousSessionService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const { instance, accounts } = useMsal();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authError, setAuthError] = useState(null);
    // Prevents duplicate loginRedirect calls when multiple API requests fail simultaneously.
    const reauthInProgressRef = useRef(false);

    // `accounts` from useMsal() is a fresh array on every render. Effects
    // and consumers must depend on a stable identifier (the homeAccountId)
    // instead, otherwise every state change re-fires every effect that
    // touches it — and a single 401 turns into thousands of retries.
    const activeAccountId = accounts[0]?.homeAccountId ?? null;

    // Initialize API call with MSAL instance
    useEffect(() => {
        initializeApiCall(instance);
    }, [instance]);

    // Clear auth errors when the signed-in account actually changes
    useEffect(() => {
        setAuthError(null);
    }, [activeAccountId]);

    // Defined before the profile-fetch effect so it can be called from within it.
    const triggerReauth = useCallback(async () => {
        if (reauthInProgressRef.current) return;
        reauthInProgressRef.current = true;
        try {
            setAuthError(null);

            // Clear MSAL accounts and tokens from local cache (no CIAM logout:
            // logoutRedirect shows the hosted "signed out" screen and was
            // avoided for session-expiry reauth).
            for (const acct of instance.getAllAccounts()) {
                try { instance.removeAccount(acct); } catch { /* ignore */ }
            }
            instance.setActiveAccount(null);

            await instance.loginRedirect({
                ...loginRequest,
                prompt: 'login',
            });
        } catch (err) {
            reauthInProgressRef.current = false;
            console.error('Re-authentication failed:', err);
            setAuthError({
                type: 'REAUTH_FAILED',
                message: 'Session expired. Please sign in again.',
                requiresReauth: true
            });
        }
    }, [instance]);

    useEffect(() => {
        const fetchOrBootstrapUserProfile = async () => {
            if (!activeAccountId) {
                setUser(null);
                setAuthError(null);
                setLoading(false);
                return;
            }

            setAuthError(null);
            const { apiCall } = await import('../../utils/apiUtils');
            if (!apiCall) {
                console.error('API call not initialized');
                setUser(accounts[0]);
                setLoading(false);
                return;
            }

            const account = accounts[0];
            const userEmail = getUserEmail(account);

            // First-login bootstrap: if no profile exists for this email, create one.
            // This used to live only in ProfilePage; centralising it here prevents a
            // 404 storm across Sidebar / billing widgets / usage timeseries on the
            // very first sign-in (notably when we migrate identity providers).
            const buildNewProfile = () => ({
                id: userEmail,
                email: userEmail,
                displayName: account.name || userEmail,
                profilePicture: '',
                subscriptionPlan: 'free',
                type: 'profile',
                usageTracking: { blogCount: 0, lastUpdated: '' },
            });

            try {
                const response = await apiCall('/get-profile-by-email', null, {
                    method: 'GET',
                    params: { email: userEmail },
                });

                setUser({
                    ...account,
                    ...response,
                    accessToken: account.accessToken,
                    isAdmin: response.role === 'admin',
                });
                // Returning users don't need the anonymous token hanging
                // around; clear it so subsequent requests go through the
                // authed (Bearer) path cleanly.
                clearAnonymousSession();
            } catch (err) {
                if (err.status === 404 || err.originalError?.response?.status === 404) {
                    try {
                        // Forward any anonymous session id so the backend can
                        // credit the user for blogs they already generated
                        // while anonymous and hand them the signup bonus.
                        const anonymousSessionId = getAnonymousSessionId();
                        const newProfile = buildNewProfile();
                        if (anonymousSessionId) {
                            newProfile.anonymousSessionId = anonymousSessionId;
                        }
                        await apiCall('/add-profile', newProfile);
                        setUser({ ...account, ...newProfile, accessToken: account.accessToken });
                        // Profile created successfully — the anon token has
                        // been claimed server-side, so drop it locally too.
                        clearAnonymousSession();
                    } catch (createErr) {
                        console.error('Profile bootstrap failed:', createErr);
                        setUser(account);
                    }
                } else if (err.type === AuthError.TOKEN_EXPIRED || err.type === AuthError.NO_ACCOUNT) {
                    // Redirect straight to the login page — showing an error box and
                    // making the user click "Sign In" is unnecessary friction.
                    setUser(null);
                    triggerReauth();
                } else if (err.type === AuthError.NETWORK_ERROR) {
                    setAuthError({ type: err.type, message: err.message, requiresReauth: false });
                    setUser(account);
                } else {
                    console.warn('Profile fetch failed, using basic account info:', err);
                    setUser(account);
                }
            }

            setLoading(false);
        };

        fetchOrBootstrapUserProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeAccountId, instance]);

    const handleAuthError = useCallback((error) => {
        if (
            error.type === AuthError.TOKEN_EXPIRED ||
            error.type === AuthError.NO_ACCOUNT ||
            error.type === AuthError.REFRESH_FAILED
        ) {
            // Auto-redirect to the login page instead of showing the error box.
            // The reauthInProgressRef guard in triggerReauth prevents duplicate
            // redirects when multiple in-flight API calls fail simultaneously.
            triggerReauth();
        } else if (error.type === AuthError.NETWORK_ERROR) {
            setAuthError({
                type: error.type,
                message: error.message,
                requiresReauth: false
            });
        }
    }, [triggerReauth]);

    const clearAuthError = useCallback(() => {
        setAuthError(null);
    }, []);

    const value = useMemo(() => ({
        user,
        loading,
        authError,
        isAuthenticated: !!user,
        handleAuthError,
        clearAuthError,
        triggerReauth,
    }), [user, loading, authError, handleAuthError, clearAuthError, triggerReauth]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
